import { LanguagesEnum } from '@/api/enums';
import { PhishingLanguagesEnum } from '@/api/enums/phishing-languages.enum';
import { handleAxiosError } from '@/api/helpers';
import { PhishingLanguageTargetingMethod } from '@/client/accounts';
import { usePublishedCampaignTemplateLanguages } from '@/hooks/query/campaign-templates.hook';
import { useToast } from '@/hooks/useToast';
import { AppAccordion } from '@/ui/accordion/AppAccordion';
import { TranslationStatusBannerCloseIcon } from '@/utils/helpers';
import { sortAlphabetically } from '@/utils/helpers/courses.helper';
import {
  getPhishingLanguageLabel,
  mapLanguageToPhishingLanguage,
} from '@/utils/helpers/phishing-language.helper';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { AccordionTab } from 'primereact/accordion';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export type LanguageTargetingValue = {
  method: PhishingLanguageTargetingMethod | null;
  singleLanguage: PhishingLanguagesEnum | null;
  fallbackLanguage: PhishingLanguagesEnum | null;
};

type LanguageOption = { label: string; value: PhishingLanguagesEnum };

const StyledCardAccordion = styled(AppAccordion)`
  &.p-accordion {
    .p-accordion-tab {
      padding: 0;
      background-color: var(--beige-main);
      border: 1px solid var(--gray-dark);
      border-radius: var(--box-medium-border-radius);
      overflow: hidden;

      .p-accordion-header .p-accordion-header-link {
        background-color: var(--beige-main);
        border: none;
        border-radius: 0;
        padding: var(--medium-padding) var(--default-padding);
      }

      .p-accordion-header .p-accordion-header-link:not(.p-disabled):hover,
      &:not(.p-disabled).p-highlight .p-accordion-header-link,
      &:not(.p-disabled).p-highlight:hover .p-accordion-header-link {
        background-color: var(--beige-main);
      }

      .p-accordion-toggle-icon {
        color: var(--black-main);
        padding-left: var(--medium-padding);
      }

      .p-accordion-content {
        display: flex;
        flex-direction: column;
        gap: var(--semi-medium-padding);
        background-color: var(--beige-main);
        padding: 0 var(--default-padding) var(--default-padding);
        border: none;
      }
    }
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxsmall-padding);
`;

const StyledTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--black-main);
`;

const StyledDescription = styled.span`
  font-size: var(--small-font-size);
  font-weight: 400;
  color: var(--black-main);
`;

const StyledOptionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
  background-color: var(--white-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--box-medium-border-radius);
  padding: var(--default-padding-small);
`;

const StyledOptionRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: var(--small-padding);
  align-items: start;
`;

const StyledCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--small-font-size);
  font-weight: 700;
  cursor: pointer;
`;

const StyledCheckboxDescription = styled.p`
  grid-column: 2;
  font-size: var(--xsmall-font-size);
  line-height: 1.5;
  margin: var(--xxxsmall-padding) 0 0 0;
  color: var(--black-main);
`;

const StyledLanguageField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--semi-xsmall-padding);
  padding-top: var(--medium-padding);
  border-top: 1px solid var(--gray-dark);
`;

const StyledFieldLabel = styled.label`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--black-main);
`;

const StyledRequired = styled.span`
  color: var(--red-main);
`;

const StyledWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--semi-medium-padding);
  padding: var(--default-padding-small) var(--default-padding)
    var(--default-padding-small) var(--default-padding-small);
  background-color: var(--yellow-pale);
  border-radius: var(--small-border-radius);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 500;
  color: var(--black-main);

  .pi-exclamation-triangle {
    color: var(--yellow-dark);
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  span {
    flex: 1;
  }
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  color: var(--black-main);

  &:hover {
    opacity: 0.7;
  }

  i {
    font-size: 12.6px;
  }
`;

const FALLBACK_PHISHING_LANGUAGE = PhishingLanguagesEnum.EN;

const isSameValue = (a: LanguageTargetingValue, b: LanguageTargetingValue) =>
  a.method === b.method &&
  a.singleLanguage === b.singleLanguage &&
  a.fallbackLanguage === b.fallbackLanguage;

const getPrefillLanguage = (
  method: PhishingLanguageTargetingMethod,
  options: LanguageOption[],
  defaultLanguage: string | null | undefined,
): PhishingLanguagesEnum | null => {
  if (!options.length) return null;

  const isAvailable = (language: PhishingLanguagesEnum | null) =>
    !!language && options.some((option) => option.value === language);

  if (method === PhishingLanguageTargetingMethod.SINGLE && defaultLanguage) {
    const mapped = mapLanguageToPhishingLanguage(
      defaultLanguage as LanguagesEnum,
    );
    if (isAvailable(mapped)) return mapped;
  }

  return isAvailable(FALLBACK_PHISHING_LANGUAGE)
    ? FALLBACK_PHISHING_LANGUAGE
    : options[0].value;
};

const withPrefill = (
  value: LanguageTargetingValue,
  options: LanguageOption[],
  defaultLanguage: string | null | undefined,
): LanguageTargetingValue => {
  if (value.method === PhishingLanguageTargetingMethod.SINGLE) {
    return value.singleLanguage
      ? value
      : {
          ...value,
          singleLanguage: getPrefillLanguage(
            value.method,
            options,
            defaultLanguage,
          ),
        };
  }

  if (value.method === PhishingLanguageTargetingMethod.AUTO) {
    return value.fallbackLanguage
      ? value
      : {
          ...value,
          fallbackLanguage: getPrefillLanguage(
            value.method,
            options,
            defaultLanguage,
          ),
        };
  }

  return value;
};

type LanguageTargetingCardProps = {
  method: PhishingLanguageTargetingMethod | null;
  singleLanguage: PhishingLanguagesEnum | null;
  fallbackLanguage: PhishingLanguagesEnum | null;
  savedValues: LanguageTargetingValue;
  defaultLanguage?: string | null;
  onChange: (value: LanguageTargetingValue) => void;
  disabled?: boolean;
};

export const LanguageTargetingCard: React.FC<LanguageTargetingCardProps> = ({
  method,
  singleLanguage,
  fallbackLanguage,
  savedValues,
  defaultLanguage,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [activeIndex, setActiveIndex] = useState<number | number[] | null>(0);
  const [dismissedValue, setDismissedValue] =
    useState<LanguageTargetingValue | null>(null);

  const {
    data: languages,
    isLoading,
    isError,
    error,
  } = usePublishedCampaignTemplateLanguages();

  const value = useMemo<LanguageTargetingValue>(
    () => ({ method, singleLanguage, fallbackLanguage }),
    [method, singleLanguage, fallbackLanguage],
  );

  const options = useMemo<LanguageOption[]>(
    () =>
      (languages ?? [])
        .map((language) => ({
          label: getPhishingLanguageLabel(language),
          value: language,
        }))
        .sort((a, b) => sortAlphabetically(a.label, b.label)),
    [languages],
  );

  useEffect(() => {
    if (isError && error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  }, [isError, error]);

  useEffect(() => {
    if (disabled) return;
    const prefilled = withPrefill(value, options, defaultLanguage);
    if (!isSameValue(prefilled, value)) {
      onChange(prefilled);
    }
  }, [value, options, defaultLanguage, disabled]);

  const isChanged = !isSameValue(value, savedValues);
  const isWarningVisible =
    isChanged && !(dismissedValue && isSameValue(dismissedValue, value));

  const handleToggle = (
    toggledMethod: PhishingLanguageTargetingMethod,
    checked: boolean,
  ) => {
    const next = { ...value, method: checked ? toggledMethod : null };
    onChange(withPrefill(next, options, defaultLanguage));
  };

  const isDropdownDisabled = disabled || isLoading || isError;

  const renderOption = (
    optionMethod: PhishingLanguageTargetingMethod,
    translationKey: 'single' | 'auto',
    fieldLabelKey: 'language' | 'fallbackLanguage',
    selectedLanguage: PhishingLanguagesEnum | null,
    languageKey: 'singleLanguage' | 'fallbackLanguage',
  ) => {
    const checkboxId = `languageTargeting-${translationKey}`;
    const dropdownId = `${checkboxId}-language`;
    const isChecked = method === optionMethod;

    return (
      <StyledOptionBox data-id={checkboxId}>
        <StyledOptionRow>
          <Checkbox
            inputId={checkboxId}
            checked={isChecked}
            disabled={disabled}
            onChange={(e) => handleToggle(optionMethod, !!e.checked)}
          />
          <StyledCheckboxLabel htmlFor={checkboxId}>
            {t(
              `account.trainingSettings.phishing.languageTargeting.${translationKey}.label`,
            )}
          </StyledCheckboxLabel>
          <StyledCheckboxDescription>
            {t(
              `account.trainingSettings.phishing.languageTargeting.${translationKey}.description`,
            )}
          </StyledCheckboxDescription>
        </StyledOptionRow>
        {isChecked && (
          <StyledLanguageField>
            <StyledFieldLabel htmlFor={dropdownId}>
              {t(
                `account.trainingSettings.phishing.languageTargeting.${translationKey}.${fieldLabelKey}`,
              )}
              <StyledRequired>*</StyledRequired>
            </StyledFieldLabel>
            <Dropdown
              inputId={dropdownId}
              value={isError ? null : selectedLanguage}
              options={options}
              onChange={(e: DropdownChangeEvent) =>
                onChange({ ...value, [languageKey]: e.value })
              }
              className="w-full"
              placeholder={t('generic.select')}
              loading={isLoading}
              disabled={isDropdownDisabled}
              {...preventDropdownBrowserTranslate}
            />
          </StyledLanguageField>
        )}
      </StyledOptionBox>
    );
  };

  return (
    <StyledCardAccordion
      activeIndex={activeIndex}
      onTabChange={(e) => setActiveIndex(e.index)}
    >
      <AccordionTab
        headerTemplate={
          <StyledHeader>
            <StyledTitle>
              {t('account.trainingSettings.phishing.languageTargeting.title')}
            </StyledTitle>
            <StyledDescription>
              {t(
                'account.trainingSettings.phishing.languageTargeting.description',
              )}
            </StyledDescription>
          </StyledHeader>
        }
      >
        {renderOption(
          PhishingLanguageTargetingMethod.SINGLE,
          'single',
          'language',
          singleLanguage,
          'singleLanguage',
        )}
        {renderOption(
          PhishingLanguageTargetingMethod.AUTO,
          'auto',
          'fallbackLanguage',
          fallbackLanguage,
          'fallbackLanguage',
        )}
        {isWarningVisible && (
          <StyledWarning data-testid="language-targeting-warning">
            <i className="pi pi-exclamation-triangle" />
            <span>
              {t('account.trainingSettings.phishing.languageTargeting.warning')}
            </span>
            <CloseButton
              type="button"
              onClick={() => setDismissedValue(value)}
              aria-label={t('generic.close')}
            >
              <TranslationStatusBannerCloseIcon />
            </CloseButton>
          </StyledWarning>
        )}
      </AccordionTab>
    </StyledCardAccordion>
  );
};
